import {useMemoizedSelector} from '../../../../../state/hooks';
import {type Ref as PanelRef} from '../../../../../state/views/panel/types';
import {type MediaBrowserPanelConfig} from '../../../../PanelMediaBrowser/common';
import {
  useMaybeWorkspaceRefsContext,
  useWorkspaceRefsContext,
} from '../../../../Workspace/WorkspaceRefsContext';
// Import from the context module directly (not the provider component) to
// avoid a module-initialization cycle through the settings hooks barrel.
import {
  useSectionSettingsContext,
  useSectionSettingsContextMaybe,
} from '../../sectionSettingsContext';
import {SettingsLevel} from '../types';
import {
  type FieldValue,
  type MediaSettingSelectorFamily,
  type ParamsArgs,
  type ResolvedValue,
} from './makeMediaSettingSelectorFamily';

/**
 * The full family of hooks for one media setting, generated from its selector
 * family. Each hook wires the selector to the workspace-refs / section-settings
 * contexts exactly as the hand-written hooks did.
 *
 * `R` mirrors the selector family's resolved type: `ResolvedValue` when the
 * spec has a `defaultValue`, otherwise `FieldValue` (may be undefined).
 */
export type MediaSettingHooks<
  K extends keyof MediaBrowserPanelConfig,
  R = ResolvedValue<K>,
  D = R,
> = {
  useWorkspace: () => FieldValue<K>;
  useMaybeWorkspace: () => FieldValue<K>;
  useSection: () => FieldValue<K>;
  useMaybeSection: () => FieldValue<K>;
  useCascadingSection: () => R;
  useMaybeCascadingSection: () => R;
  usePanel: (panelRef: PanelRef | undefined) => FieldValue<K>;
  useDisplay: (settingsLevel: SettingsLevel, panelRef?: PanelRef) => D;
  /**
   * Whether to show the reset-to-parent button for this field at the current
   * settings level. Product decision: the reset button is never shown at the
   * workspace level, so this always returns false there.
   */
  useShouldShowReset: (
    settingsLevel: SettingsLevel,
    panelRef?: PanelRef
  ) => boolean;
};

export function makeMediaSettingHooks<
  K extends keyof MediaBrowserPanelConfig,
  R,
  D = R,
  P = void,
>(
  selectors: MediaSettingSelectorFamily<K, R, D, P>,
  /**
   * Hook supplying the caller params the selector family needs (e.g.
   * `isSingleRun` from context for gallery/grid defaults). Required when the
   * family was built with a params type.
   */
  useParams?: () => P
): MediaSettingHooks<K, R, D> {
  const useParamsArgs: () => ParamsArgs<P> =
    useParams == null
      ? () => [] as ParamsArgs<P>
      : () => [useParams()] as ParamsArgs<P>;

  const useWorkspace = (): FieldValue<K> => {
    const {workspaceSettingsRef} = useWorkspaceRefsContext();
    return useMemoizedSelector(
      selectors.makeWorkspaceSelector,
      workspaceSettingsRef
    );
  };

  const useMaybeWorkspace = (): FieldValue<K> => {
    const workspaceRefs = useMaybeWorkspaceRefsContext();
    return useMemoizedSelector(
      selectors.makeWorkspaceSelector,
      workspaceRefs?.workspaceSettingsRef
    );
  };

  const useSection = (): FieldValue<K> => {
    const {sectionRef} = useSectionSettingsContext();
    return useMemoizedSelector(selectors.makeSectionSelector, sectionRef);
  };

  const useMaybeSection = (): FieldValue<K> => {
    const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
    return useMemoizedSelector(selectors.makeSectionSelector, sectionRef);
  };

  const useCascadingSection = (): R => {
    const {workspaceSettingsRef} = useWorkspaceRefsContext();
    const {sectionRef} = useSectionSettingsContext();
    const params = useParamsArgs();
    return useMemoizedSelector(
      selectors.makeCascadingSectionSelector,
      workspaceSettingsRef,
      sectionRef,
      ...params
    );
  };

  const useMaybeCascadingSection = (): R => {
    const workspaceRefs = useMaybeWorkspaceRefsContext();
    const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
    const params = useParamsArgs();
    return useMemoizedSelector(
      selectors.makeCascadingSectionSelector,
      workspaceRefs?.workspaceSettingsRef,
      sectionRef,
      ...params
    );
  };

  const usePanel = (panelRef: PanelRef | undefined): FieldValue<K> =>
    useMemoizedSelector(selectors.makePanelSelector, panelRef);

  const useDisplay = (settingsLevel: SettingsLevel, panelRef?: PanelRef): D => {
    const workspaceRefs = useMaybeWorkspaceRefsContext();
    const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
    const params = useParamsArgs();
    return useMemoizedSelector(
      selectors.makeDisplaySelector,
      workspaceRefs?.workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef,
      ...params
    );
  };

  const useShouldShowReset = (
    settingsLevel: SettingsLevel,
    panelRef?: PanelRef
  ): boolean => {
    const sectionValue = useMaybeSection();
    const panelValue = usePanel(panelRef);
    if (settingsLevel === SettingsLevel.PANEL) {
      return panelValue != null;
    }
    return settingsLevel === SettingsLevel.SECTION && sectionValue != null;
  };

  return {
    useWorkspace,
    useMaybeWorkspace,
    useSection,
    useMaybeSection,
    useCascadingSection,
    useMaybeCascadingSection,
    usePanel,
    useDisplay,
    useShouldShowReset,
  };
}
